import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {add, fetch, update} from '../services/BillingProvidersService';
import {
    buildCreateBillingProviderInput,
    buildUpdateBillingProviderInput
} from '../services/buildBillingProviderInput';
import {BillingProvider} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications.tsx';
import {billingProvidersQueryKeys} from './billingProvidersQueryKeys';

export const useBillingProvider = (billingProviderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const billingProviderQuery = useQuery({
        queryKey: billingProvidersQueryKeys.detail(billingProviderId),
        queryFn: () => fetch(billingProviderId!),
        enabled: !!billingProviderId,
        staleTime: 30_000,
    });

    // ['billing-providers'] is the root of the list AND the detail keys, so one
    // invalidation reaches both (§8).
    const invalidate = () => queryClient.invalidateQueries({queryKey: billingProvidersQueryKeys.all});

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: BillingProvider) => add(buildCreateBillingProviderInput(values)),
        onSuccess: () => {
            notifySuccess(I18n.get('Billing Provider Added'));
            invalidate();
        },
        onError: (error) => notifyError(error),
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: BillingProvider) => {
            if (!billingProviderId) throw new Error('billingProviderId is required parameter');
            return update(buildUpdateBillingProviderInput(billingProviderId, values));
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Billing Provider Updated'));
            invalidate();
        },
        onError: (error) => notifyError(error),
    });

    // Resolve true once the save lands and false when it is rejected, never throw: the
    // drawer awaits this and closes only on true, so a server-side rejection keeps the
    // operator's input on screen (§6, §7). The mutation's onError has already toasted.
    const settle = (run: Promise<unknown>) => run.then(() => true, () => false);

    return {
        record: billingProviderQuery.data,
        loading: billingProviderQuery.isLoading,
        isError: billingProviderQuery.isError,
        createRecord: (values: BillingProvider) => settle(createMutation.mutateAsync(values)),
        updateRecord: (values: BillingProvider) => settle(updateMutation.mutateAsync(values)),
        createMutation,
        updateMutation,
    };
};
